import { isBefore } from 'date-fns/isBefore';
import { isPast } from 'date-fns/isPast';
import { parseISO } from 'date-fns/parseISO';
import { startOfDay } from 'date-fns/startOfDay';
import { DisputeStatus } from 'uplisting-frontend/models/dispute';
import { SecurityDepositStatus } from 'uplisting-frontend/models/security-deposit';
import { SafeguestVerificationStatus } from 'uplisting-frontend/models/booking';
import { BookingRentalAgreementStatus } from 'uplisting-frontend/models/booking-rental-agreement';
import { GuestIdentityVerificationStatus } from 'uplisting-frontend/models/occasion';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export enum ProtectionState {
  needs = 'needs',
  expired = 'expired',
  waiting = 'waiting',
  onTrack = 'on_track',
  claimed = 'claimed',
  failed = 'failed',
  settled = 'settled',
  na = 'na',
}

// The states a booking on the protection page can reach. `failed` belongs to a
// dispute, so it stays out of the page's status filter.
export const OCCASION_STATES: ProtectionState[] = [
  ProtectionState.needs,
  ProtectionState.expired,
  ProtectionState.waiting,
  ProtectionState.onTrack,
  ProtectionState.claimed,
  ProtectionState.settled,
  ProtectionState.na,
];

const RANK: Record<ProtectionState, number> = {
  [ProtectionState.needs]: 7,
  [ProtectionState.expired]: 6,
  [ProtectionState.waiting]: 5,
  [ProtectionState.onTrack]: 4,
  [ProtectionState.claimed]: 3,
  [ProtectionState.failed]: 2,
  [ProtectionState.settled]: 1,
  [ProtectionState.na]: 0,
};

export enum DepositOutcome {
  claimed = 'claimed',
  partiallyClaimed = 'partially_claimed',
  released = 'released',
  autoReleased = 'auto_released',
  expired = 'expired',
}

export type DepositReading = SecurityDepositStatus | DepositOutcome;

const OUTCOME_STATES: Record<DepositOutcome, ProtectionState> = {
  [DepositOutcome.claimed]: ProtectionState.claimed,
  [DepositOutcome.partiallyClaimed]: ProtectionState.claimed,
  [DepositOutcome.released]: ProtectionState.settled,
  [DepositOutcome.autoReleased]: ProtectionState.settled,
  [DepositOutcome.expired]: ProtectionState.needs,
};

const DEPOSIT_STATES: Record<SecurityDepositStatus, ProtectionState> = {
  [SecurityDepositStatus.needsPaymentInformation]: ProtectionState.needs,
  [SecurityDepositStatus.failed]: ProtectionState.needs,
  [SecurityDepositStatus.failing]: ProtectionState.waiting,
  [SecurityDepositStatus.hasPaymentInformation]: ProtectionState.onTrack,
  [SecurityDepositStatus.authorized]: ProtectionState.onTrack,
  [SecurityDepositStatus.collected]: ProtectionState.claimed,
  [SecurityDepositStatus.overridden]: ProtectionState.settled,
};

const SAFEGUEST_STATES: Record<SafeguestVerificationStatus, ProtectionState> = {
  [SafeguestVerificationStatus.pending]: ProtectionState.waiting,
  [SafeguestVerificationStatus.green]: ProtectionState.settled,
  [SafeguestVerificationStatus.yellow]: ProtectionState.settled,
  [SafeguestVerificationStatus.red]: ProtectionState.settled,
};

const IDENTITY_STATES: Record<
  GuestIdentityVerificationStatus,
  ProtectionState
> = {
  [GuestIdentityVerificationStatus.requiresInput]: ProtectionState.needs,
  [GuestIdentityVerificationStatus.requiresAction]: ProtectionState.needs,
  [GuestIdentityVerificationStatus.processing]: ProtectionState.waiting,
  [GuestIdentityVerificationStatus.verified]: ProtectionState.settled,
  [GuestIdentityVerificationStatus.succeeded]: ProtectionState.settled,
  [GuestIdentityVerificationStatus.overridden]: ProtectionState.settled,
};

const AGREEMENT_STATES: Record<BookingRentalAgreementStatus, ProtectionState> =
  {
    [BookingRentalAgreementStatus.pending]: ProtectionState.needs,
    [BookingRentalAgreementStatus.processing]: ProtectionState.waiting,
    [BookingRentalAgreementStatus.processed]: ProtectionState.settled,
    [BookingRentalAgreementStatus.markedAsSigned]: ProtectionState.settled,
  };

const DISPUTE_STATES: Record<DisputeStatus, ProtectionState> = {
  [DisputeStatus.needsResponse]: ProtectionState.needs,
  [DisputeStatus.underReview]: ProtectionState.waiting,
  [DisputeStatus.won]: ProtectionState.settled,
  [DisputeStatus.lost]: ProtectionState.failed,
  [DisputeStatus.closed]: ProtectionState.settled,
  [DisputeStatus.prevented]: ProtectionState.settled,
};

const CHIP_CLASSES: Record<ProtectionState, string> = {
  [ProtectionState.needs]: 'protection-chip-needs',
  [ProtectionState.expired]: 'protection-chip-expired',
  [ProtectionState.waiting]: 'protection-chip-waiting',
  [ProtectionState.onTrack]: 'protection-chip-on-track',
  [ProtectionState.claimed]: 'protection-chip-claimed',
  [ProtectionState.failed]: 'protection-chip-failed',
  [ProtectionState.settled]: 'protection-chip-settled',
  [ProtectionState.na]: 'protection-chip-na',
};

function lookup<T extends string>(
  states: Record<T, ProtectionState>,
  value: T | undefined | null,
): ProtectionState {
  if (!value) {
    return ProtectionState.na;
  }

  return states[value] ?? ProtectionState.waiting;
}

function isDepositExpired(occasion: ModelRegistry['occasion']): boolean {
  const { securityDepositStatus, securityDepositExpiresAt } = occasion;

  return (
    securityDepositStatus === SecurityDepositStatus.authorized &&
    !occasion.securityDepositReleasedAt &&
    !!securityDepositExpiresAt &&
    isPast(securityDepositExpiresAt)
  );
}

function expiredBeforeCheckOut(occasion: ModelRegistry['occasion']): boolean {
  const { securityDepositExpiresAt, checkOut } = occasion;

  return (
    !!securityDepositExpiresAt &&
    !!checkOut &&
    isBefore(securityDepositExpiresAt, new Date(checkOut))
  );
}

/**
 * Mirrors the combinations `Ui::Booking::Status::SecurityDeposit` derives on the
 * booking page, so a deposit reads the same wherever it is shown.
 */
export function depositReading(
  occasion: ModelRegistry['occasion'],
): DepositReading | undefined {
  const status = occasion.securityDepositStatus;

  if (!status) {
    return undefined;
  }

  const claimed = occasion.securityDepositClaimedAmount ?? 0;

  if (claimed > 0) {
    return claimed >= occasion.securityDepositAmount
      ? DepositOutcome.claimed
      : DepositOutcome.partiallyClaimed;
  }

  if (occasion.securityDepositReleasedAt) {
    return DepositOutcome.released;
  }

  if (isDepositExpired(occasion)) {
    return expiredBeforeCheckOut(occasion)
      ? DepositOutcome.expired
      : DepositOutcome.autoReleased;
  }

  return status;
}

export function depositState(
  reading: DepositReading | undefined,
  safeguest: SafeguestVerificationStatus | undefined,
): ProtectionState {
  if (!reading) {
    return lookup(SAFEGUEST_STATES, safeguest);
  }

  return (
    OUTCOME_STATES[reading as DepositOutcome] ??
    lookup(DEPOSIT_STATES, reading as SecurityDepositStatus)
  );
}

export function identityState(
  status: GuestIdentityVerificationStatus | undefined,
): ProtectionState {
  return lookup(IDENTITY_STATES, status);
}

export function agreementState(
  status: BookingRentalAgreementStatus | undefined,
): ProtectionState {
  return lookup(AGREEMENT_STATES, status);
}

export function disputeState(
  status: DisputeStatus | undefined,
): ProtectionState {
  return lookup(DISPUTE_STATES, status);
}

export function chipClass(state: ProtectionState): string {
  return CHIP_CLASSES[state];
}

export function worstState(states: ProtectionState[]): ProtectionState {
  return states.reduce(
    (worst, state) => (RANK[state] > RANK[worst] ? state : worst),
    ProtectionState.na,
  );
}

function isStayOver(occasion: ModelRegistry['occasion']): boolean {
  const { checkOut } = occasion;

  return (
    !!checkOut && isBefore(parseISO(String(checkOut)), startOfDay(new Date()))
  );
}

/**
 * Once the guest has left there is nothing left to chase, so what still needed
 * the host reads as expired rather than as an open task.
 */
export function occasionState(
  occasion: ModelRegistry['occasion'],
): ProtectionState {
  const state = worstState([
    depositState(depositReading(occasion), occasion.safeguestStatus),
    identityState(occasion.guestIdentityVerificationStatus),
    agreementState(occasion.rentalAgreementStatus),
  ]);

  if (state === ProtectionState.needs && isStayOver(occasion)) {
    return ProtectionState.expired;
  }

  return state;
}
